import Head from 'next/head'
import React from 'react'
import AdaptiveThemeBox from '../../atoms/AdaptiveThemeBox'
import Container from '../../molecules/Container'
import HeaderBar from '../../organisms/HeaderBar'
import BackLink from '../../molecules/BackLink'
import ThemeBox from '../../atoms/ThemeBox'
import ThemedBackground from '../../atoms/ThemedBackground'
import Footer from '../../organisms/Footer'
import { contentPathToURLs } from '../../utils/contentPathToURLs'
import { CasesPages, CasesShared } from '../../content/types'
import { useLocale } from '../../hooks/useLocale'
import ProjectsContent from '../../organisms/ProjectsContent'
import MetaInformation from '../../utils/MetaInformation'
import { GetStaticPaths } from 'next'
import ContactUsCTA from '@/organisms/ContactCTA'
import Image from 'next-export-optimize-images/image'

type StaticPathType = {
  params: {
    slug: string
  }
}

export const getStaticPaths: GetStaticPaths = async (context) => {
  // the only paths that are allowed will be slugs that have a corresponding markdown
  // file in the content/cases directory. the `fallback: false` means that a 404
  // will be generated for all other urls. moreover, this method is required
  // for use with the `next export` command, which generates the static site.
  let paths: StaticPathType[] = contentPathToURLs('content/cases').map((f) => {
    return { params: { slug: f } }
  })

  if (context.locales) {
    paths = paths.flatMap((path) =>
      context.locales.map((locale) => ({ ...path, locale })),
    )
  }

  return { paths: paths, fallback: false }
}

export async function getStaticProps({ params }: StaticPathType) {
  // this is safe because of the getStaticPaths function, which only allows slugs
  // that match these files.
  // changing the corresponding markdown file will not trigger a live-reload in
  // nextjs, but the alternative is using an entirely different markdown parser.
  const { attributes } = require(`../../content/cases/${params.slug}.md`)
  const { attributes: shared_attributes } = require(
    `../../content/cases_shared.md`,
  )

  return { props: { attributes, shared_attributes, slug: params.slug } }
}

export default function ProjectsPageSingle({ attributes, shared_attributes }) {
  const { translations, locale } = useLocale<CasesPages>(attributes)
  const { translations: shared_translations } =
    useLocale<CasesShared>(shared_attributes)

  const headerThemeClass =
    translations.header_img_theme === 'dark' ? 'transp-black' : 'transp-light'

  return (
    <>
      <Head>
        {/* NOTE: the <title> tag gets its contents through string interpolation. If we use JSX syntax to insert <t>Abl | {t.name}</t> the */}
        {/* JSX compiler will embed an empty html comment due to the way {} gets expanded, which causes it to show up in titles. */}
        <title>{`Researchable | ${translations.name}`}</title>
        <MetaInformation locale={locale} {...translations.meta} />
      </Head>
      <ThemeBox theme={headerThemeClass}>
        <div className={'relative bg-gradient-to-b transp-black:from-black/50'}>
          <Image
            fill
            alt={translations.name}
            src={translations.header_img}
            className="object-cover w-full -z-10"
            priority
          />
          <AdaptiveThemeBox defaultTheme={headerThemeClass}>
            <HeaderBar />
          </AdaptiveThemeBox>
          <ThemeBox theme={headerThemeClass}>
            <ThemedBackground>
              <Container>
                <div className="py-10">
                  {/* TODO: add image */}
                  <BackLink href="/projects">
                    {shared_translations.overview}
                  </BackLink>
                  <div className="lg:mx-30 py-20 text-center md:text-left">
                    <h1 className="font-medium xl:text-huge lg:text-6xl text-5xl leading-relaxed">
                      {translations.name}
                    </h1>
                  </div>
                </div>
              </Container>
            </ThemedBackground>
          </ThemeBox>
        </div>
      </ThemeBox>

      <ThemeBox theme="bright">
        <ThemedBackground>
          <Container>
            <ProjectsContent content={translations.content} />
            <ContactUsCTA />
          </Container>
        </ThemedBackground>
      </ThemeBox>
      <ThemeBox theme="black">
        <Footer />
      </ThemeBox>
    </>
  )
}
